import {
	ChangeEvent, useCallback, useEffect, useMemo, useRef, useState
} from 'react';
import {
	CARGO_TYPE_ID, SHIPMENT_EDIT_WINDOW, TCargoForm, TRaisedClaim, TShipmentOrder
} from '@types';
import { usePostPriceAdjustment } from '@api';
import { isEmpty } from '@utils';
import { debounce } from 'lodash';

export const defaultCargoForm: TCargoForm = {
	pallet_quantities: null,
	pallet_spaces: null,
	total_weight: null,
	width: null,
	height: null,
	length: null,
	use_user_input_cost: false,
	user_manually_cost: null,
	user_manually_fuel_levy: null,
	use_global_fuel_levy: false,
};

type TSubmitOptions = {
	onSuccess?: (raisedClaims: TRaisedClaim[]) => void;
	onError?: (errPayload: { errors?: Record<string, string[]>; message: string }) => void;
}

type TCargoFormOptions = {
	onFormChange?: () => void;
	onRecalcSuccess?: () => void;
	onRecalcError?: (errPayload: { errors?: Record<string, string[]>; message: string }) => void;
}

/* A quote is worked out but never applied, so it accepts neither figure. */
const QUOTE_ACCEPTANCE = { accept_shipper_price: false, accept_carrier_cost: false };

export const useCargoForm = (shipment: TShipmentOrder | null, options: TCargoFormOptions = {}) => {
	const { onFormChange, onRecalcSuccess, onRecalcError } = options;

	const onRecalcSuccessRef = useRef(onRecalcSuccess);
	onRecalcSuccessRef.current = onRecalcSuccess;

	const onRecalcErrorRef = useRef(onRecalcError);
	onRecalcErrorRef.current = onRecalcError;

	const [form, setForm] = useState<TCargoForm>(defaultCargoForm);
	const [recalculated, setRecalculated] = useState(false);
	const [newShipperPrice, setNewShipperPrice] = useState(0);
	const [newCarrierCost, setNewCarrierCost] = useState(0);
	const [newCarrierManualCost, setNewCarrierManualCost] = useState(0);
	const [oldShipperPrice, setOldShipperPrice] = useState(0);
	const [oldCarrierCost, setOldCarrierCost] = useState(0);
	const [editWindow, setEditWindow] = useState<SHIPMENT_EDIT_WINDOW>();

	const { loadingPriceAdjustment, callPriceAdjustment } = usePostPriceAdjustment();

	const isFLTorPallets = shipment?.cargo_type_id === CARGO_TYPE_ID.Pallets || shipment?.cargo_type_id === CARGO_TYPE_ID.FullTruckload;
	const isOther = shipment?.cargo_type_id === CARGO_TYPE_ID.Other;
	const isLoose = shipment?.cargo_type_id === CARGO_TYPE_ID.Loose;
	const isItem = shipment?.cargo_type_id === CARGO_TYPE_ID.CustomItem;
	const isFtl = shipment?.cargo_type_id === CARGO_TYPE_ID.FullTruckload;
	const isAllocated = shipment?.status?.short !== 'Pending';

	const totalPallets = shipment?.cargos?.reduce((t, c) => t + (c.pallet_qty || 0), 0) ?? 0;
	const totalSpaces = shipment?.cargos?.reduce((t, c) => t + (c.qty || 0), 0) ?? 0;
	const totalWeight = shipment?.cargos?.reduce((t, c) => t + (c.total_weight || 0), 0) ?? 0;
	const itemWeight = shipment?.cargos?.[0]?.cargo_item?.weight_kg ?? 0;

	const updateForm = (e: ChangeEvent<HTMLInputElement>) => {
		const { name, value, checked } = e.target;
		const checkFields = ['use_user_input_cost', 'use_global_fuel_levy'];
		setForm((prev) => ({ ...prev, [name]: checkFields.includes(name) ? checked : value }));
		onFormChange?.();
	};

	const updateQty = (e: ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		const updateObj: Record<string, any> = {};
		if (name === 'pallet_quantities' && isItem) {
			updateObj['total_weight'] = `${Number(value) * itemWeight}`;
		}
		setForm((prev) => ({
			...prev, [name]: value, ...updateObj
		}));
		onFormChange?.();
	};

	const canUpdate = useMemo(() => {
		if (isFLTorPallets) return !isEmpty(form.pallet_quantities, { stringToNum: true }) && !isEmpty(form.total_weight, { stringToNum: true });
		if (isLoose) return !isEmpty(form.total_weight, { stringToNum: true });
		if (isItem) return !isEmpty(form.pallet_quantities, { stringToNum: true });
		if (isOther) {
			return !isEmpty(form.pallet_spaces, { stringToNum: true }) &&
				!isEmpty(form.total_weight, { stringToNum: true }) &&
				!isEmpty(form.width, { stringToNum: true }) &&
				!isEmpty(form.height, { stringToNum: true }) &&
				!isEmpty(form.length, { stringToNum: true });
		}
		return false;
	}, [form, isFLTorPallets, isLoose, isItem, isOther]);

	/* The edit applies to whichever side has a figure to apply: there is nothing for an admin to tick. */
	const acceptShipperPrice = newShipperPrice > 0;
	const acceptCarrierCost = isAllocated && newCarrierCost > 0;
	/* A stated cost applies on its own: it is offered precisely when the carrier has no rate card to cost
	   from, so accepting a rate-card cost alongside it would accept a figure that was never worked out. */
	const hasStatedCost = form.use_user_input_cost && !isEmpty(form.user_manually_cost, { stringToNum: true });

	const canSubmit = useMemo(
		() => canUpdate && (acceptShipperPrice || acceptCarrierCost || hasStatedCost || isFtl),
		[canUpdate, acceptShipperPrice, acceptCarrierCost, hasStatedCost, isFtl],
	);

	const reCalc = useCallback(debounce((shipmentId: number | undefined, payload: TCargoForm) => {
		callPriceAdjustment({
			routes: [`${shipmentId}`],
			payload: {
				...payload, ...QUOTE_ACCEPTANCE, is_confirmed: false
			},
		})
			.then((res) => {
				if (res) {
					const { data } = res.data;
					setEditWindow(data?.edit_window);
					setNewShipperPrice(data?.shipper?.new?.price?.total_price ?? 0);
					if (payload.use_user_input_cost) {
						setNewCarrierManualCost(data?.carrier?.new?.price?.total_price ?? 0);
					} else {
						setNewCarrierCost(data?.carrier?.new?.price?.total_price ?? 0);
					}
					/* What the shipper is charged now, not what their invoice says: past the invoice the price
					   on record stops moving and the differences sit in approved I&Cs, so striking through the
					   record would show a figure nobody is billed. */
					setOldShipperPrice(
						data?.shipper?.current_total_price
							?? data?.shipper?.original?.price?.total_price
							?? 0,
					);
					setOldCarrierCost(data?.carrier?.original?.price?.total_price ?? 0);
					setRecalculated(true);
					onRecalcSuccessRef.current?.();
				}
			})
			.catch((err) => {
				if (err?.cause) onRecalcErrorRef.current?.(err.cause.response.data);
			});
	}, 1000), []);

	const resetForm = () => {
		setForm({ ...defaultCargoForm, cargo_id: shipment?.cargos?.[0]?.id });
		setRecalculated(false);
		setNewShipperPrice(0);
		setNewCarrierCost(0);
		setNewCarrierManualCost(0);
		setOldShipperPrice(0);
		setOldCarrierCost(0);
		setEditWindow(undefined);
	};

	const submitCargo = (opts: TSubmitOptions = {}) => {
		const payload: any = {
			...form,
			accept_shipper_price: acceptShipperPrice,
			accept_carrier_cost: acceptCarrierCost,
			is_confirmed: true,
			is_ftl: isFtl,
		};

		callPriceAdjustment({ routes: [`${shipment?.id}`], payload })
			.then((res) => {
				if (res) {
					/* Read before resetting: past the shipper's invoice the difference goes to an I&C rather
					   than to the price, one per half of the price that moved, and this is the only place
					   the caller is told that happened. */
					const raisedClaims = res.data?.data?.raised_claims ?? [];
					resetForm();
					opts.onSuccess?.(raisedClaims);
				}
			})
			.catch((err) => {
				if (err?.cause) opts.onError?.(err.cause.response.data);
			});
	};

	useEffect(() => {
		if (canUpdate) {
			reCalc(shipment?.id, form);
		} else {
			setRecalculated(false);
			setNewShipperPrice(0);
			setNewCarrierCost(0);
			setNewCarrierManualCost(0);
			setOldShipperPrice(0);
			setOldCarrierCost(0);
			setEditWindow(undefined);
		}
	}, [
		form.pallet_quantities,
		form.pallet_spaces,
		form.total_weight,
		form.width,
		form.height,
		form.length,
		form.user_manually_cost,
		form.user_manually_fuel_levy,
		form.use_global_fuel_levy,
		canUpdate,
	]);

	useEffect(() => {
		if (shipment) {
			setForm((prev) => ({ ...prev, cargo_id: shipment.cargos?.[0]?.id }));
		}
	}, [shipment]);

	return {
		form,
		updateForm,
		updateQty,
		isFLTorPallets,
		isOther,
		isLoose,
		isItem,
		isFtl,
		isAllocated,
		totalPallets,
		totalSpaces,
		totalWeight,
		canUpdate,
		canSubmit,
		recalculated,
		newShipperPrice,
		newCarrierCost,
		newCarrierManualCost,
		oldShipperPrice,
		oldCarrierCost,
		loadingPriceAdjustment,
		editWindow,
		resetForm,
		submitCargo,
	};
};
